
import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { Heart, MessageCircle, Check, X, Bell } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
    const [items, setItems] = useState([]);
    const [interests, setInterests] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [n, i] = await Promise.all([
                client.get("/notifications"),
                client.get("/interests?box=received"),
            ]);
            setItems(n.data);
            setInterests(i.data.filter((x) => x.status === "pending"));
            await client.post("/notifications/read-all").catch(() => {});
        } catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const respond = async (interestId, action) => {
        try {
            await client.post(`/interests/${interestId}/respond?action=${action}`);
            toast.success(`Interest ${action}ed`);
            await load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Activity</div>
            <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Notifications</h1>

            {/* Interest requests */}
            <div className="mt-10">
                <h2 className="font-serif text-2xl text-navy mb-4">Pending Interests</h2>
                {interests.length === 0 ? (
                    <div className="bg-linen rounded-2xl p-6 text-sm font-sans text-navy-500">No pending interests.</div>
                ) : (
                    <div className="space-y-3">
                        {interests.map((i) => (
                            <div key={i.interest_id} className="bg-white border border-navy-100 rounded-2xl p-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-gold/20 text-gold-700 flex items-center justify-center"><Heart className="h-4 w-4" /></div>
                                    <div>
                                        <div className="font-sans font-semibold text-navy text-sm">{i.profile?.full_name || i.profile?.name}</div>
                                        <div className="text-xs text-navy-400 font-sans">expressed interest in your profile</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => respond(i.interest_id, "accept")} className="font-sans text-xs uppercase tracking-wider bg-gold text-navy hover:bg-gold-600 hover:text-white rounded-lg px-4 py-2 transition-all flex items-center gap-1">
                                        <Check className="h-3.5 w-3.5" /> Accept
                                    </button>
                                    <button onClick={() => respond(i.interest_id, "decline")} className="font-sans text-xs uppercase tracking-wider border border-navy-200 text-navy hover:bg-navy hover:text-white rounded-lg px-4 py-2 transition-all flex items-center gap-1">
                                        <X className="h-3.5 w-3.5" /> Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Recent activity */}
            <div className="mt-10">
                <h2 className="font-serif text-2xl text-navy mb-4">Recent Activity</h2>
                {loading ? <div className="text-navy-400">Loading…</div> : items.length === 0 ? (
                    <div className="bg-linen rounded-2xl p-6 text-sm font-sans text-navy-500">No activity yet.</div>
                ) : (
                    <div className="space-y-2">
                        {items.map((n) => (
                            <div key={n.notification_id} className="bg-white border border-navy-50 rounded-xl p-4 flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-linen text-gold-700 flex items-center justify-center">
                                    {n.type.includes("message") ? <MessageCircle className="h-4 w-4" /> : n.type.includes("interest") ? <Heart className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                                </div>
                                <div className="flex-1">
                                    <div className="font-sans text-sm text-navy">
                                        <span className="font-semibold">{n.from_name || "Someone"}</span>
                                        {n.type === "interest_received" && " expressed interest in your profile"}
                                        {n.type === "interest_accepted" && " accepted your interest 💍"}
                                        {n.type === "interest_declined" && " declined your interest"}
                                        {n.type === "message_received" && " sent you a message"}
                                    </div>
                                    <div className="text-xs text-navy-400 font-sans">{new Date(n.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
